import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import SafeRichText from '../components/SafeRichText';
import api from '../apiReequest';
import LoadingSpinner from '../components/LoadingSpinner';
import { FaHandsHelping, FaChartLine, FaProjectDiagram, FaUsers, FaRegCalendarAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';

// Helper to format ISO date to DD/MM/YYYY format
function formatDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// Helper to resolve an image source from an object that may contain image_url
function resolveImageSrc(item, apiBase) {
  if (!item || !item.image_url) return null;
  
  const imageUrl = String(item.image_url);
  
  // If it's already an absolute URL, return as-is
  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl;
  }
  
  // If it starts with /, prefix with API base
  if (imageUrl.startsWith('/')) {
    return `${apiBase}${imageUrl}`;
  }
  
  // Otherwise prefix with apiBase
  return `${apiBase}/${imageUrl}`;
}

// Hero Section - Full image display without cropping
function HeroSection() {
  const heroRef = useRef(null);

  return (
    <section ref={heroRef} className="relative w-full flex items-center justify-center rounded-b-3xl rounded-3xl overflow-hidden bg-[#0b1220]">
      {/* Image container - maintains aspect ratio */}
      <div className="w-full">
        <img
          src="/images/1_VPED_team_Photoroom.JPG"
          alt="VenturePoint team"
          className="w-full h-auto block"
          style={{ display: 'block' }}
        />
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-800/30 to-slate-900/40 pointer-events-none rounded-b-3xl" />
      
      {/* Content overlay - responsive text sizing */}
      <div className="absolute inset-0 z-10 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="text-center w-full max-w-5xl">
          <motion.h1
            className="text-lg sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold italic text-white mb-2 sm:mb-6 lg:mb-8 leading-tight sm:leading-[1.1] tracking-tight drop-shadow-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <br />WHERE VISION MEETS<br />IMPLEMENTATION
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
          >
            <Link
              to="/services"
              className="w-auto inline-flex items-center justify-center border-2 border-white/20 hover:border-white/40 text-white px-3 sm:px-8 py-2 sm:py-4 rounded-lg text-sm sm:text-lg lg:text-xl font-semibold transition-all duration-300 backdrop-blur-sm hover:bg-white/10"
            >
              Our Services
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// About Section - Container adapts to image, no cropping
function AboutSection() {
  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Grid with items-center to vertically align */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 items-center">
          {/* Image Column - Natural size, no cropping */}
          <motion.div 
            className="w-full"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/images/2_VPED_team_2.jpg"
              alt="Team collaboration"
              className="w-full h-auto rounded-lg shadow-xl"
            />
          </motion.div>
          
          {/* Text Column - Vertically centered to match image */}
          <motion.div 
            className="w-full"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col justify-center">
              <h2 className="text-base sm:text-xl lg:text-2xl xl:text-3xl font-bold text-slate-800 mb-3 sm:mb-4">
                About VenturePoint for Economic Development (VPED)
              </h2>
              <h3 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-slate-800 mb-3 sm:mb-4">
                Built on Legacy. Driven by Impact.
              </h3>
              <p className="text-sm sm:text-base lg:text-lg xl:text-xl text-slate-700 leading-relaxed">
                As the development landscape evolves, VenturePoint bridges donors and governments, policy and practice, vision and action. With decades of experience, we deliver evidence-based, practical solutions that advance inclusive and sustainable growth.
              </p>
            </div>
          </motion.div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-8 sm:mt-10 lg:mt-12">
          <Link 
            to="/about" 
            className="inline-block bg-[#3a8b9e] hover:bg-[#2d6d7d] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Learn More
          </Link>
        </div>
      </div>
    </section>
  );
}

// Services Section - Responsive grid
function ServicesSection() {
  const services = [
    {
      icon: FaChartLine,
      title: "Strategic Advisory",
      description: "Expert guidance for development initiatives"
    },
    {
      icon: FaProjectDiagram,
      title: "Program Design & Evaluation",
      description: "Evidence-based program development"
    },
    {
      icon: FaHandsHelping,
      title: "Partnership Facilitation",
      description: "Building effective collaborations"
    },
    {
      icon: FaUsers,
      title: "Capacity Development",
      description: "Empowering local institutions"
    }
  ];

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-10 sm:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-3 sm:mb-4">
            What We Do
          </h2>
        </motion.div>

        {/* Responsive service cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-6 xl:gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              className="text-center px-4 py-6 sm:px-6 sm:py-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="flex justify-center mb-4 sm:mb-6">
                <service.icon className="text-5xl sm:text-6xl lg:text-5xl xl:text-6xl text-[#2e3b2e]" />
              </div>
              <h3 className="text-lg sm:text-xl lg:text-lg xl:text-xl font-bold text-slate-800 mb-2 sm:mb-3">
                {service.title}
              </h3>
              <p className="text-slate-600 text-sm sm:text-base lg:text-sm xl:text-base leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10 sm:mt-12 lg:mt-16">
          <Link
            to="/services"
            className="inline-block bg-[#3a8b9e] hover:bg-[#2d6d7d] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
          >
            Explore Our Services
          </Link>
        </div>
      </div>
    </section>
  );
}

// Projects Section - Consistent card heights
function ProjectsSection({ projects, apiBase }) {
  const projectImages = useMemo(() => [
    '/images/DefaultImage.png',
    '/images/DefaultImage.png',
    '/images/DefaultImage.png'
  ], []);

  const latestProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    return projects;
  }, [projects]);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-2 sm:mb-3 lg:mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-3 sm:mb-4">
            Experience That Drives Our Vision
          </h2>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto mb-10 sm:mb-12 lg:mb-16 text-center px-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <p className="text-base sm:text-lg lg:text-xl text-slate-700 leading-relaxed">
            VenturePoint builds on the extensive experience of its founders, who have led and contributed to transformative USAID-funded programs in Egypt. While these programs were achieved before establishing the firm, they deeply influence how we bridge policy and practice, donor priorities and national needs, and vision and implementation.
          </p>
        </motion.div>

        {/* Responsive project cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 xl:gap-8">
          {latestProjects.map((project, index) => (
            <Link to={`/projects/${project.id}`} key={project.id}>
              <motion.div
                className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 h-full shadow-md flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {/* Image container with consistent aspect ratio */}
                <div className="relative w-full overflow-hidden bg-slate-200" style={{ paddingBottom: '66.67%' }}>
                  {(() => {
                    const imgSrc = resolveImageSrc(project, apiBase || '') || projectImages[index % projectImages.length];
                    return (
                      <img
                        src={imgSrc}
                        alt={project.title || project.name}
                        className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => { 
                          e.currentTarget.src = projectImages[index % projectImages.length];
                        }}
                      />
                    );
                  })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>
                
                {/* Content area with flex-grow for consistent heights */}
                <div className="p-4 sm:p-5 lg:p-6 flex-grow flex flex-col">
                  <h3 className="text-lg sm:text-xl lg:text-lg xl:text-xl font-bold text-slate-800 mb-2 sm:mb-3 line-clamp-2">
                    {project.title || project.name}
                  </h3>
                  <div className="text-slate-600 text-sm sm:text-base line-clamp-3 flex-grow">
                    <SafeRichText content={project.description || project.content || ""} />
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10 sm:mt-12 lg:mt-16">
          <Link
            to="/projects"
            className="inline-block bg-[#3a8b9e] hover:bg-[#2d6d7d] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
          >
            View All Projects
          </Link>
        </div>
      </div>
    </section>
  );
}

// Events Section - Consistent card heights
function EventsSection({ events, apiBase }) {
  const eventImages = useMemo(() => [
    '/images/DefaultImage.png',
    '/images/DefaultImage.png'
  ], []);

  const topEvents = useMemo(() => {
    if (!Array.isArray(events)) return [];
    return events.slice(0, 3);
  }, [events]);

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-10 sm:mb-12 lg:mb-16" 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-3 sm:mb-4">
            Latest Events
          </h2>
        </motion.div>

        {/* Responsive event cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 xl:gap-8">
          {topEvents.map((e, idx) => (
            <Link to={`/events/${e.id}`} key={e.id || idx}>
              <motion.div 
                className="group bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 h-full shadow-md cursor-pointer flex flex-col" 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                whileHover={{ y: -5 }}
              >
                {/* Image container with consistent aspect ratio */}
                <div className="relative w-full overflow-hidden bg-slate-200" style={{ paddingBottom: '66.67%' }}>
                  {(() => {
                    const imgSrc = resolveImageSrc(e, apiBase || '') || eventImages[idx % eventImages.length];
                    return (
                      <img
                        src={imgSrc}
                        alt={e.title || e.name}
                        className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(ev) => { 
                          ev.currentTarget.src = eventImages[idx % eventImages.length];
                        }}
                      />
                    );
                  })()}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>
                
                {/* Content area with flex-grow for consistent heights */}
                <div className="p-4 sm:p-5 lg:p-6 flex-grow flex flex-col">
                  <div className="flex items-center gap-3 mb-3 sm:mb-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#e8f1f7] rounded-md flex items-center justify-center flex-shrink-0">
                      <FaRegCalendarAlt className="text-lg sm:text-xl text-[#3a8b9e]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-base sm:text-lg text-slate-800 line-clamp-1">
                        {e.title || e.name}
                      </div>
                      <div className="text-sm sm:text-base text-[#3a8b9e]">
                        {formatDate(e.event_date || e.date || '')}
                      </div>
                    </div>
                  </div>
                  <div className="text-slate-600 text-sm sm:text-base line-clamp-3 flex-grow">
                    <SafeRichText content={e.description || e.content || ''} />
                  </div>
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10 sm:mt-12 lg:mt-16">
          <Link 
            to="/events" 
            className="inline-block bg-[#3a8b9e] hover:bg-[#2d6d7d] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
          >
            View All Events
          </Link>
        </div>
      </div>
    </section>
  );
}

// Partner CTA Section - Container adapts to image
function PartnerCTASection() {
  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[#2F3A36] rounded-t-3xl rounded-3xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">
          {/* Text Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center md:text-left"
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
              Become Our Partner
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-300 mb-6 sm:mb-8 leading-relaxed">
              Let's build the future of development — together.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#3a8b9e] hover:bg-[#2d6d7d] text-white px-6 sm:px-8 py-3 sm:py-3.5 rounded-lg text-base sm:text-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Partner With Us
            </Link>
          </motion.div>
          
          {/* Image Column - Natural size, no cropping */}
          <motion.div 
            className="overflow-hidden rounded-lg shadow-xl"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/images/Image_3_Home.jpeg"
              alt="Partnership"
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Main Home Component
export default function Home() {
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';
  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchData = async () => {
      try {
        // Measure time for projects endpoint
        const projectsStart = performance.now();
        const projectsRes = await api.get('/projects?limit=3');
        const projectsEnd = performance.now();
        const projectsTime = ((projectsEnd - projectsStart) / 1000).toFixed(3);
        console.log(`⏱️ PROJECTS endpoint took: ${projectsTime}s`);

        // Measure time for events endpoint
        const eventsStart = performance.now();
        const eventsRes = await api.get('/events?limit=3');
        const eventsEnd = performance.now();
        const eventsTime = ((eventsEnd - eventsStart) / 1000).toFixed(3);
        console.log(`⏱️ EVENTS endpoint took: ${eventsTime}s`);

        console.log('Projects payload:', projectsRes.data);
        console.log('Events payload:', eventsRes.data);

        // Support APIs that return either an array directly or an object with a `data` array
        const projectsArray = Array.isArray(projectsRes.data) ? projectsRes.data : (projectsRes.data?.data ?? []);
        const eventsArray = Array.isArray(eventsRes.data) ? eventsRes.data : (eventsRes.data?.data ?? []);

        setProjects(projectsArray);
        setEvents(eventsArray);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      
      {/* Show loading state for dynamic sections */}
      {loading ? (
        <LoadingSpinner message="Loading content..." />
      ) : error ? (
        <div className="py-16 sm:py-20 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-red-600 font-semibold text-lg">⚠️ Error loading content:</p>
            <p className="text-slate-600 mt-2 text-base">{error}</p>
          </div>
        </div>
      ) : (
        <>
          <ProjectsSection projects={projects} apiBase={API_BASE_URL} />
          <EventsSection events={events} apiBase={API_BASE_URL} />
        </>
      )}
      
      <PartnerCTASection />
    </div>
  );
}